// Scenario state shared by every page. One reducer, persisted per tab in sessionStorage,
// so moving between Decision, Sensitivity, Uncertainty and Advisor always analyses the
// same inputs.
import { createContext, useContext, useEffect, useMemo, useReducer, type ReactNode } from 'react';
import type { OverrideValue, RunRequest, ScenarioInput } from '../types/api';

export const DEFAULT_SCENARIO: ScenarioInput = {
  capacity_tpa: 25_000,
  route: 'sulphate_roast',
  location: 'odisha',
  ecta: true,
  sc6_fob_usd_t: null,
  lioh_price_usd_t: null,
  wacc: null,
  gas_price_override_usd_gj: null,
  power_price_override_inr_kwh: null,
  capex_factor_override: null,
  feed_pricing: 'fixed',
  price_basis: 'benchmark',
  ramp_case: 'base',
  debt_share: null,
  overrides: {},
};

export type RouteMode = 'sulphate_roast' | 'soda_ash_pressure_leach' | 'both';

export interface ScenarioState {
  scenario: ScenarioInput;
  routeMode: RouteMode;
  tieTolerance: number;
}

const INITIAL: ScenarioState = { scenario: DEFAULT_SCENARIO, routeMode: 'sulphate_roast', tieTolerance: 0.03 };
const STORAGE_KEY = 'pde.scenario.v2';

type Action =
  | { type: 'patch'; patch: Partial<ScenarioInput> }
  | { type: 'routeMode'; mode: RouteMode }
  | { type: 'tieTolerance'; value: number }
  | { type: 'setOverride'; path: string; value: OverrideValue }
  | { type: 'clearOverride'; path: string }
  | { type: 'load'; request: RunRequest }
  | { type: 'reset' };

function reducer(state: ScenarioState, action: Action): ScenarioState {
  switch (action.type) {
    case 'patch':
      return { ...state, scenario: { ...state.scenario, ...action.patch } };
    case 'routeMode': {
      const route = action.mode === 'both' ? state.scenario.route : action.mode;
      return { ...state, routeMode: action.mode, scenario: { ...state.scenario, route } };
    }
    case 'tieTolerance':
      return { ...state, tieTolerance: action.value };
    case 'setOverride':
      return { ...state, scenario: { ...state.scenario, overrides: { ...state.scenario.overrides, [action.path]: action.value } } };
    case 'clearOverride': {
      const next = { ...state.scenario.overrides };
      delete next[action.path];
      return { ...state, scenario: { ...state.scenario, overrides: next } };
    }
    case 'load': {
      const routes = action.request.routes ?? null;
      const routeMode: RouteMode = routes && routes.length > 1 ? 'both' : ((routes?.[0] ?? action.request.scenario.route) as RouteMode);
      return { scenario: { ...DEFAULT_SCENARIO, ...action.request.scenario }, routeMode, tieTolerance: action.request.tie_tolerance_pct ?? 0.03 };
    }
    case 'reset':
      return INITIAL;
  }
}

function restore(): ScenarioState {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return INITIAL;
    const parsed = JSON.parse(raw) as ScenarioState;
    return { ...INITIAL, ...parsed, scenario: { ...DEFAULT_SCENARIO, ...parsed.scenario } };
  } catch {
    return INITIAL;
  }
}

export function toRunRequest(state: ScenarioState, includeCashflow = true): RunRequest {
  return {
    scenario: state.scenario,
    routes: state.routeMode === 'both' ? ['sulphate_roast', 'soda_ash_pressure_leach'] : [state.routeMode],
    tie_tolerance_pct: state.tieTolerance,
    include_cashflow: includeCashflow,
  };
}

interface Ctx {
  state: ScenarioState;
  dispatch: React.Dispatch<Action>;
  runRequest: RunRequest;
}

const ScenarioContext = createContext<Ctx | null>(null);

export function ScenarioProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(reducer, undefined, restore);
  useEffect(() => {
    try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch { /* storage unavailable */ }
  }, [state]);
  const runRequest = useMemo(() => toRunRequest(state), [state]);
  const value = useMemo(() => ({ state, dispatch, runRequest }), [state, runRequest]);
  return <ScenarioContext.Provider value={value}>{children}</ScenarioContext.Provider>;
}

export function useScenario(): Ctx {
  const ctx = useContext(ScenarioContext);
  if (!ctx) throw new Error('useScenario must be used inside <ScenarioProvider>.');
  return ctx;
}

export function overrideCount(s: ScenarioInput): number {
  return Object.keys(s.overrides).length;
}
